/**
 * useSEOSettings — fetches global SEO settings from the DB and injects
 * GTM / GA4 scripts into the document <head>.
 *
 * Replaces the hardcoded GTM-MTRZV5G and any inline GA4 snippet.
 */
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type Settings = Record<string, string>;

export const useSEOSettings = (): Settings => {
  const { data: settings = {} } = useQuery<Settings>({
    queryKey: ["seo-settings"],
    queryFn: async () => {
      const { data, error } = await supabase.from("seo_settings").select("key, value");
      if (error) throw error;
      const map: Settings = {};
      (data ?? []).forEach((r: { key: string; value: string | null }) => {
        map[r.key] = r.value ?? "";
      });
      return map;
    },
    staleTime: 1000 * 60 * 10, // 10 min cache
  });

  return settings;
};

const INJECTED_MARKER = "data-diw-analytics";

export const AnalyticsInjector = () => {
  const settings = useSEOSettings();

  useEffect(() => {
    // Remove any previously injected analytics tags so we can re-inject with fresh IDs
    document.querySelectorAll(`[${INJECTED_MARKER}]`).forEach((el) => el.remove());

    const gtm = settings.gtm_container_id?.trim();
    const ga4 = settings.ga4_measurement_id?.trim();

    // ── GTM ──────────────────────────────────────────────────────────────────
    if (gtm) {
      // dataLayer init + GTM snippet
      const dataLayerScript = document.createElement("script");
      dataLayerScript.setAttribute(INJECTED_MARKER, "gtm-datalayer");
      dataLayerScript.textContent = `window.dataLayer=window.dataLayer||[];window.dataLayer.push({'gtm.start':new Date().getTime(),event:'gtm.js'});`;
      document.head.appendChild(dataLayerScript);

      const gtmScript = document.createElement("script");
      gtmScript.setAttribute(INJECTED_MARKER, "gtm-script");
      gtmScript.async = true;
      gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${gtm}`;
      document.head.appendChild(gtmScript);

      // GTM noscript iframe (body)
      if (!document.querySelector(`[${INJECTED_MARKER}="gtm-noscript"]`)) {
        const noscript = document.createElement("noscript");
        noscript.setAttribute(INJECTED_MARKER, "gtm-noscript");
        noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtm}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
        document.body.insertBefore(noscript, document.body.firstChild);
      }
    }

    // ── GA4 (standalone — only if GTM not handling it) ───────────────────────
    if (ga4 && !gtm) {
      const ga4Script = document.createElement("script");
      ga4Script.setAttribute(INJECTED_MARKER, "ga4-script");
      ga4Script.async = true;
      ga4Script.src = `https://www.googletagmanager.com/gtag/js?id=${ga4}`;
      document.head.appendChild(ga4Script);

      const ga4Init = document.createElement("script");
      ga4Init.setAttribute(INJECTED_MARKER, "ga4-init");
      ga4Init.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${ga4}');`;
      document.head.appendChild(ga4Init);
    }
  }, [settings.gtm_container_id, settings.ga4_measurement_id]);

  return null;
};
